import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { canManageTracker } from '../tracker/trackerModel';
import { collection, query, onSnapshot, addDoc, serverTimestamp, doc, updateDoc, deleteDoc, getDocs, where, arrayRemove } from '../../lib/auditedFirestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext'; 
import { logAuditAction } from '../../lib/auditLogger'; 
import Spinner from '../../components/Spinner';
import { jsPDF } from 'jspdf';
// --- FIXED IMPORT LOGIC ---
import autoTable from 'jspdf-autotable';

const ConfirmationModal = ({ message, onConfirm, onCancel }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-8 rounded-lg shadow-2xl max-w-md w-full text-center">
            <p className="mb-6 text-gray-700">{message}</p>
            <div className="flex justify-center gap-4">
                <button
                    onClick={onCancel}
                    className="bg-gray-300 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-400"
                >
                    Cancel
                </button>
                <button
                    onClick={onConfirm}
                    className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600"
                >
                    Confirm
                </button>
            </div>
        </div>
    </div>
);

const DuplicatePaperModal = ({ paper, onConfirm, onCancel }) => {
    const [newName, setNewName] = useState(`${paper.name} - Copy`);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        await onConfirm(newName);
        setIsSubmitting(false);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-8 rounded-lg shadow-2xl max-w-md w-full">
                <h3 className="text-xl font-bold mb-4 text-gray-800">Duplicate Paper</h3>
                <form onSubmit={handleSubmit}>
                    <div className="mb-6">
                        <label className="block text-gray-700 text-sm font-bold mb-2">New Paper Name</label>
                        <input
                            type="text"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                            autoFocus
                        />
                    </div>
                    <div className="flex justify-end gap-4">
                        <button
                            type="button"
                            onClick={onCancel}
                            disabled={isSubmitting}
                            className="bg-gray-300 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-400 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting || !newName.trim()}
                            className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                        >
                            {isSubmitting ? <Spinner color="border-white" /> : 'Duplicate'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const EditPaperModal = ({ paper, onClose, setModal }) => {
    const { user } = useAuth(); 
    const [paperName, setPaperName] = useState(paper.name);
    const [paperLevel, setPaperLevel] = useState(paper.level || 'skill');
    const [isSaving, setIsSaving] = useState(false);

    // AI Global Settings for this Paper
    const [aiReportEnabled, setAiReportEnabled] = useState(paper.aiReportEnabled || false);

    // Formula Sheet State
    const [formulaSheetType, setFormulaSheetType] = useState(paper.formulaSheetType || 'none');
    const [formulaSheetContent, setFormulaSheetContent] = useState(
        paper.formulaSheetType === 'image' ? (paper.formulaSheetContent || []) : (paper.formulaSheetContent || '')
    );
    const [isUploading, setIsUploading] = useState(false);

    // Sections State - Expanded for Hybrid AI Mapping
    const [sections, setSections] = useState(paper.sections || []);

    const handleSectionChange = (index, field, value) => {
        const newSections = [...sections];
        newSections[index][field] = value;
        setSections(newSections);
    };

    const addSection = () => {
        setSections([...sections, { 
            name: `Section ${String.fromCharCode(65 + sections.length)}`, 
            title: '', 
            topic: '', 
            skillType: 'calculation', // AI Context: calculation, theory, analysis
            numQuestions: 0, 
            marksPerQuestion: 0,
            improvementAdvice: '',
            aiContextModifier: '' // Specific instructions for AI on this topic
        }]);
    };

    const removeSection = (index) => {
        setSections(sections.filter((_, i) => i !== index));
    };

    const handleImageUpload = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;
        setIsUploading(true);
        try {
            const uploadPromises = files.map(file => {
                const storageRef = ref(storage, `formula-sheets/${paper.id}/${Date.now()}-${file.name}`);
                return uploadBytes(storageRef, file).then(snapshot => getDownloadURL(snapshot.ref));
            });
            const downloadURLs = await Promise.all(uploadPromises);
            setFormulaSheetContent(prevContent => [...(prevContent || []), ...downloadURLs]);
            setModal({ show: true, message: `${files.length} image(s) uploaded successfully.` });
        } catch (error) {
            console.error("Formula sheet image upload failed:", error);
            setModal({ show: true, message: `Image upload failed: ${error.message}` });
        } finally {
            setIsUploading(false);
        }
    };
    
    const handleRemoveImage = (indexToRemove) => {
        setFormulaSheetContent(prevContent => prevContent.filter((_, index) => index !== indexToRemove));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!paperName.trim()) {
            setModal({ show: true, message: "Paper name cannot be empty." });
            return;
        }
        setIsSaving(true);
        try {
            const paperRef = doc(db, 'papers', paper.id);
            const dataToUpdate = {
                name: paperName,
                level: paperLevel,
                aiReportEnabled: aiReportEnabled,
                formulaSheetType: formulaSheetType,
                sections: sections,
            };

            if (formulaSheetType === 'text') {
                dataToUpdate.formulaSheetContent = formulaSheetContent || '';
            } else if (formulaSheetType === 'image') {
                dataToUpdate.formulaSheetContent = formulaSheetContent || [];
            } else {
                dataToUpdate.formulaSheetContent = ''; 
            }

            await updateDoc(paperRef, dataToUpdate);

            logAuditAction(user, 'EDIT_PAPER', `Edited paper '${paperName}' (Level: ${paperLevel})`, { id: paper.id });

            setModal({ show: true, message: "Paper and Syllabus updated successfully!" });
            onClose();
        } catch (error) {
            console.error("Error updating paper:", error);
            setModal({ show: true, message: `Error: ${error.message}` });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-800">Syllabus Designer & AI Analytics</h3>
                    <div className="flex items-center gap-3 bg-purple-50 p-2 rounded-lg border border-purple-200">
                        <span className="text-sm font-bold text-purple-700">Enable InstilAI Reports:</span>
                        <button 
                            type="button"
                            onClick={() => setAiReportEnabled(!aiReportEnabled)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${aiReportEnabled ? 'bg-purple-600' : 'bg-gray-300'}`}
                        >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${aiReportEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSave} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-gray-700 font-bold mb-2">Paper Name</label>
                            <input
                                type="text"
                                value={paperName}
                                onChange={(e) => setPaperName(e.target.value)}
                                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-bold mb-2">Paper Level</label>
                            <select
                                value={paperLevel}
                                onChange={(e) => setPaperLevel(e.target.value)}
                                className="w-full p-2 border rounded-lg bg-white"
                            >
                                <option value="skill">Skill Level</option>
                                <option value="professional">Professional Level</option>
                            </select>
                        </div>
                    </div>
                    
                    <div className="pt-4 border-t">
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <label className="block text-gray-700 font-bold">Syllabus Structure (Topic Definitions)</label>
                                <p className="text-xs text-gray-500">Weightage is exam-specific. Define areas here for mistake mapping.</p>
                            </div>
                            <button type="button" onClick={addSection} className="bg-purple-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-purple-700 transition">
                                + Add Syllabus Area
                            </button>
                        </div>

                        <div className="space-y-6">
                            {sections.map((section, index) => (
                                <div key={index} className="p-5 border-2 border-gray-100 rounded-xl bg-gray-50/50 space-y-4 relative">
                                    <button 
                                        type="button" 
                                        onClick={() => removeSection(index)} 
                                        className="absolute top-4 right-4 text-red-400 hover:text-red-600 font-bold text-xl"
                                    >
                                        &times;
                                    </button>

                                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                                        <div className="md:col-span-2">
                                            <label className="block text-xs font-bold text-gray-600 uppercase">Section ID</label>
                                            <input type="text" value={section.name} onChange={(e) => handleSectionChange(index, 'name', e.target.value)} className="w-full p-2 border rounded bg-white" placeholder="e.g. Sec A"/>
                                        </div>
                                        <div className="md:col-span-6">
                                            <label className="block text-xs font-bold text-gray-600 uppercase">Syllabus Topic Name</label>
                                            <input type="text" value={section.topic} onChange={(e) => handleSectionChange(index, 'topic', e.target.value)} className="w-full p-2 border rounded bg-white font-semibold" placeholder="e.g. IAS 16 PPE"/>
                                        </div>
                                        <div className="md:col-span-4">
                                            <label className="block text-xs font-bold text-gray-600 uppercase">Primary Skill (For AI Analysis)</label>
                                            <select 
                                                value={section.skillType} 
                                                onChange={(e) => handleSectionChange(index, 'skillType', e.target.value)} 
                                                className="w-full p-2 border rounded bg-white text-purple-700 font-medium"
                                            >
                                                <option value="calculation">Numerical/Calculation</option>
                                                <option value="theory">Theoretical Knowledge</option>
                                                <option value="analysis">Application & Analysis</option>
                                                <option value="professional">Professional Skepticism</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-600 uppercase">Static Improvement Advice (Standard)</label>
                                            <textarea 
                                                value={section.improvementAdvice} 
                                                onChange={(e) => handleSectionChange(index, 'improvementAdvice', e.target.value)} 
                                                className="w-full p-2 border rounded text-sm h-20 bg-white" 
                                                placeholder="General steps like: 'Review study text chapter 4...'"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-purple-600 uppercase">AI Tutor Context (Optional Modifier)</label>
                                            <textarea 
                                                value={section.aiContextModifier} 
                                                onChange={(e) => handleSectionChange(index, 'aiContextModifier', e.target.value)} 
                                                className="w-full p-2 border border-purple-100 rounded text-sm h-20 bg-purple-50/30" 
                                                placeholder="Tell InstilAI what to focus on for mistakes in this area (e.g. 'Check for double-entry logic errors')"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex gap-4 items-end border-t pt-3">
                                        <div className="w-48">
                                            <label className="block text-xs font-bold text-gray-500 italic">Planned Questions (Reference)</label>
                                            <input type="number" value={section.numQuestions} onChange={(e) => handleSectionChange(index, 'numQuestions', Number(e.target.value))} className="w-full p-1 border rounded bg-white" placeholder="0"/>
                                        </div>
                                        <div className="w-48">
                                            <label className="block text-xs font-bold text-gray-500 italic">Target Marks (Reference)</label>
                                            <input type="number" value={section.marksPerQuestion} onChange={(e) => handleSectionChange(index, 'marksPerQuestion', Number(e.target.value))} className="w-full p-1 border rounded bg-white" placeholder="0"/>
                                        </div>
                                        <p className="text-[10px] text-gray-400 self-center">* Actual weights are calculated from exam tags.</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="pt-4 border-t">
                        <label className="block text-gray-700 font-bold mb-2">Formula Sheet Reference</label>
                        <div className="flex gap-4 mb-3">
                            {['none', 'text', 'image'].map(type => (
                                <label key={type} className="flex items-center cursor-pointer">
                                    <input 
                                        type="radio" 
                                        name="formulaType" 
                                        value={type} 
                                        checked={formulaSheetType === type} 
                                        onChange={(e) => { 
                                            setFormulaSheetType(e.target.value); 
                                            setFormulaSheetContent(type === 'image' ? [] : ''); 
                                        }} 
                                        className="mr-2 accent-purple-600" 
                                    /> 
                                    <span className="capitalize text-sm font-medium">{type}</span>
                                </label>
                            ))}
                        </div>
                        {formulaSheetType === 'text' && (
                            <textarea
                                value={formulaSheetContent}
                                onChange={(e) => setFormulaSheetContent(e.target.value)}
                                className="w-full p-2 border rounded-lg h-32 focus:ring-2 focus:ring-purple-500"
                                placeholder="Paste standard formulas here..."
                            />
                        )}
                        {formulaSheetType === 'image' && (
                            <div>
                                <input type="file" accept="image/*" onChange={handleImageUpload} multiple className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"/>
                                {isUploading && <p className="text-sm text-purple-600 animate-pulse mt-2">Uploading images...</p>}
                                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                                    {Array.isArray(formulaSheetContent) && formulaSheetContent.map((url, index) => (
                                        <div key={index} className="relative group border p-1 rounded-lg">
                                            <img src={url} alt={`Formula Sheet ${index + 1}`} className="rounded-md w-full h-32 object-contain bg-gray-100" />
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveImage(index)}
                                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full h-6 w-6 flex items-center justify-center text-xs shadow-lg"
                                            >
                                                &times;
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-4 mt-8 pt-6 border-t">
                        <button type="button" onClick={onClose} className="bg-gray-200 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-300 transition">Cancel</button>
                        <button type="submit" disabled={isSaving || isUploading} className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-2 rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 shadow-md">
                            {isSaving ? <Spinner /> : 'Save Syllabus & AI Settings'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- ADDED onViewInstilIQ TO PROPS ---
const PaperListAdmin = ({ onViewExams, onViewQuestionBank, onViewLectures, onViewHomework, onViewInstilIQ, setModal, assignedPapers, allowedFeatures }) => {
    const navigate = useNavigate();
    const { userData: trackerProfile } = useAuth();
    const { user } = useAuth();
    const [papers, setPapers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [newPaperName, setNewPaperName] = useState('');
    const [newPaperLevel, setNewPaperLevel] = useState('skill');
    const [isAdding, setIsAdding] = useState(false);
    const [paperToDelete, setPaperToDelete] = useState(null);
    const [paperToEdit, setPaperToEdit] = useState(null);
    const [paperToDuplicate, setPaperToDuplicate] = useState(null);

    useEffect(() => {
        const q = query(collection(db, "papers"));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            let fetchedPapers = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // --- RBAC FILTERING LOGIC ---
            if (assignedPapers) {
                if (assignedPapers.length > 0) {
                    fetchedPapers = fetchedPapers.filter(paper => assignedPapers.includes(paper.id));
                } else {
                    fetchedPapers = []; 
                }
            }

            setPapers(fetchedPapers);
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [assignedPapers]); 

    const handleAddPaper = async (e) => {
        e.preventDefault();
        if (!newPaperName.trim()) return;
        setIsAdding(true);
        try {
            const docRef = await addDoc(collection(db, 'papers'), {
                name: newPaperName,
                level: newPaperLevel, 
                aiReportEnabled: false,
                createdAt: serverTimestamp()
            });
            logAuditAction(user, 'CREATE_PAPER', `Created paper '${newPaperName}'`, { id: docRef.id });
            setNewPaperName('');
        } catch (error) {
            console.error(error);
            setModal({ show: true, message: "Error adding paper." });
        } finally {
            setIsAdding(false);
        }
    };

    const confirmDelete = async () => {
        if (!paperToDelete) return;
        try {
            const paperObj = papers.find(p => p.id === paperToDelete);
            const studentsQuery = query(collection(db, "users"), where("role", "==", "student"), where("assignedPapers", "array-contains", paperToDelete));
            const querySnapshot = await getDocs(studentsQuery);
            const updatePromises = querySnapshot.docs.map(studentDoc => 
                updateDoc(doc(db, 'users', studentDoc.id), { assignedPapers: arrayRemove(paperToDelete) })
            );
            await Promise.all(updatePromises);
            await deleteDoc(doc(db, 'papers', paperToDelete));
            logAuditAction(user, 'DELETE_PAPER', `Deleted paper '${paperObj?.name}'`, { id: paperToDelete });
            setModal({ show: true, message: 'Paper and student access removed.' });
        } catch (error) {
            console.error(error);
            setModal({ show: true, message: "Delete failed." });
        } finally {
            setPaperToDelete(null);
        }
    };

    const handleDuplicatePaper = async (newName) => {
        if (!paperToDuplicate) return;
        try {
            // 1. Copy the Paper Document
            const originalData = { ...paperToDuplicate };
            const oldPaperId = originalData.id;
            delete originalData.id;
            
            // Assign the new configuration
            originalData.name = newName;
            originalData.createdAt = serverTimestamp();

            const newPaperRef = await addDoc(collection(db, 'papers'), originalData);
            const newPaperId = newPaperRef.id;

            // 2. DEEP COPY: Copy Questions
            const qBankQuery = query(collection(db, "questions"), where("paperId", "==", oldPaperId));
            const qBankSnapshot = await getDocs(qBankQuery);
            const qPromises = qBankSnapshot.docs.map(docSnap => {
                const qData = docSnap.data();
                return addDoc(collection(db, 'questions'), { ...qData, paperId: newPaperId, createdAt: serverTimestamp() });
            });

            // 3. DEEP COPY: Copy Lectures
            const lecturesQuery = query(collection(db, "lectures"), where("paperId", "==", oldPaperId));
            const lecturesSnapshot = await getDocs(lecturesQuery);
            const lPromises = lecturesSnapshot.docs.map(docSnap => {
                const lData = docSnap.data();
                return addDoc(collection(db, 'lectures'), { ...lData, paperId: newPaperId, createdAt: serverTimestamp() });
            });

            // 4. DEEP COPY: Copy Homework
            const homeworkQuery = query(collection(db, "homeworks"), where("paperId", "==", oldPaperId));
            const homeworkSnapshot = await getDocs(homeworkQuery);
            const hPromises = homeworkSnapshot.docs.map(docSnap => {
                const hData = docSnap.data();
                return addDoc(collection(db, 'homeworks'), { ...hData, paperId: newPaperId, createdAt: serverTimestamp() });
            });

            // 5. DEEP COPY: Copy Exams
            const examsQuery = query(collection(db, "exams"), where("paperId", "==", oldPaperId));
            const examsSnapshot = await getDocs(examsQuery);
            const ePromises = examsSnapshot.docs.map(docSnap => {
                const eData = docSnap.data();
                return addDoc(collection(db, 'exams'), { ...eData, paperId: newPaperId, createdAt: serverTimestamp() });
            });

            // Execute all duplication promises simultaneously
            await Promise.all([...qPromises, ...lPromises, ...hPromises, ...ePromises]);
            
            logAuditAction(user, 'DUPLICATE_PAPER', `Deep Duplicated paper '${paperToDuplicate.name}' to create '${newName}' with all contents.`, { id: newPaperId });
            setModal({ show: true, message: `Successfully created ${newName} and copied all associated content!` });
        } catch (error) {
            console.error("Duplication failed:", error);
            setModal({ show: true, message: `Duplication failed: ${error.message}` });
        } finally {
            setPaperToDuplicate(null);
        }
    };

    const handleDownloadSyllabusReport = async (paper) => {
        // --- UPDATED LOGIC TO AVOID TYPEERROR ---
        const doc = new jsPDF();
        doc.setFontSize(20);
        doc.setTextColor(217, 59, 138);
        doc.text(`Syllabus Reference Map: ${paper.name}`, 105, 20, { align: "center" });
        
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Academic Director Control Panel | Date: ${new Date().toLocaleDateString()}`, 105, 28, { align: "center" });

        const tableBody = (paper.sections || []).map(s => [
            s.name,
            s.topic || "N/A",
            s.skillType?.toUpperCase() || "N/A",
            `${s.marksPerQuestion || 0} Target Marks`,
            paper.aiReportEnabled ? "AI Ready" : "Static Only"
        ]);

        autoTable(doc, {
            startY: 40,
            head: [['ID', 'Syllabus Area', 'Skill Focus', 'Reference Target', 'Analysis Mode']],
            body: tableBody,
            headStyles: { fillColor: [139, 92, 246] }
        });

        const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 15 : 100;
        
        doc.setFontSize(14);
        doc.setTextColor(0);
        doc.text("Improvement Analysis Framework", 20, finalY);

        const logicBody = (paper.sections || []).map(s => [
            s.topic || s.name,
            s.improvementAdvice || "No static advice defined.",
            s.aiContextModifier || "Default AI instructions will be used."
        ]);

        autoTable(doc, {
            startY: finalY + 5,
            head: [['Topic Area', 'Admin Suggestion (Fixed)', 'AI Tutor Modifier']],
            body: logicBody,
            styles: { fontSize: 8 },
            columnStyles: { 1: { cellWidth: 80 }, 2: { cellWidth: 60 } }
        });

        doc.save(`${paper.name}_Syllabus_Mapping.pdf`);
    };

    if (isLoading) return <div className="flex justify-center mt-8"><Spinner color="border-purple-500" /></div>;

    // --- EMPTY STATE HANDLING ---
    if (papers.length === 0 && assignedPapers) {
        return (
           <div className="text-center mt-10 p-12 bg-white border border-gray-200 rounded-lg shadow-sm">
               <h3 className="text-2xl font-bold text-gray-700">No Papers Assigned</h3>
               <p className="text-gray-500 mt-2">You have not been granted access to manage any specific ACCA papers yet.</p>
               <p className="text-gray-400 text-sm mt-1">Please contact the Super Admin to update your permissions.</p>
           </div>
        );
    }

    return (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
            {paperToDelete && (
                <ConfirmationModal
                    message="Delete paper? Student access will be revoked immediately."
                    onConfirm={confirmDelete}
                    onCancel={() => setPaperToDelete(null)}
                />
            )}
            {paperToEdit && (
                <EditPaperModal
                    paper={paperToEdit}
                    onClose={() => setPaperToEdit(null)}
                    setModal={setModal}
                />
            )}
            {paperToDuplicate && (
                <DuplicatePaperModal
                    paper={paperToDuplicate}
                    onConfirm={handleDuplicatePaper}
                    onCancel={() => setPaperToDuplicate(null)}
                />
            )}

            <div className="flex flex-col md:flex-row justify-between items-end gap-4 mb-8">
                <div className="w-full">
                    <h2 className="text-2xl font-bold text-gray-800">Paper Management</h2>
                    <p className="text-gray-500 text-sm">Design syllabus topics and configure AI report generation.</p>
                </div>
                
                {/* Conditionally hide the Add Paper form if they are a restricted admin */}
                {(!assignedPapers) && (
                    <form onSubmit={handleAddPaper} className="flex gap-2 w-full md:w-auto">
                        <input
                            type="text"
                            value={newPaperName}
                            onChange={(e) => setNewPaperName(e.target.value)}
                            placeholder="e.g. FR, PM, FM"
                            className="p-2 border rounded-lg grow"
                        />
                        <select
                            value={newPaperLevel}
                            onChange={(e) => setNewPaperLevel(e.target.value)}
                            className="p-2 border rounded-lg bg-gray-50 text-sm"
                        >
                            <option value="skill">Skill</option>
                            <option value="professional">Professional</option>
                        </select>
                        <button type="submit" disabled={isAdding} className="bg-purple-600 text-white px-4 py-2 rounded-lg font-bold">
                            {isAdding ? '...' : 'Add'}
                        </button>
                    </form>
                )}
            </div>

            <div className="grid grid-cols-1 gap-4">
                {papers.map(paper => (
                    <div key={paper.id} className="p-5 border rounded-xl flex flex-col md:flex-row justify-between items-center gap-4 hover:border-purple-200 transition bg-white shadow-sm">
                        <div className="flex-grow">
                            <div className="flex items-center gap-3">
                                <h3 className="font-extrabold text-xl text-gray-800">{paper.name}</h3>
                                {paper.aiReportEnabled && (
                                    <span className="bg-purple-100 text-purple-700 text-[10px] uppercase font-black px-2 py-0.5 rounded-full border border-purple-200">
                                        InstilAI Powered
                                    </span>
                                )}
                            </div>
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">{paper.level} LEVEL</p>
                            <div className="mt-2 flex gap-4">
                                <span className="text-xs font-semibold text-gray-500">📂 {paper.sections?.length || 0} Syllabus Areas</span>
                                <button onClick={() => handleDownloadSyllabusReport(paper)} className="text-xs text-blue-600 hover:text-blue-800 font-bold underline decoration-blue-200">
                                    Export Syllabus Report
                                </button>
                            </div>
                        </div>
                        
                        <div className="flex flex-wrap justify-center gap-2">
                            <button onClick={() => onViewQuestionBank(paper)} className="bg-blue-50 text-blue-700 font-bold py-1.5 px-3 rounded-lg hover:bg-blue-100 text-xs transition">📚 Q-Bank</button>
                            <button onClick={() => onViewLectures(paper)} className="bg-teal-50 text-teal-700 font-bold py-1.5 px-3 rounded-lg hover:bg-teal-100 text-xs transition">🎓 Lectures</button>
                            <button onClick={() => onViewHomework(paper)} className="bg-orange-50 text-orange-700 font-bold py-1.5 px-3 rounded-lg hover:bg-orange-100 text-xs transition">📝 Homework</button>
                            
                            {canManageTracker(trackerProfile, paper.id) && <button onClick={() => navigate(`/tracker/${encodeURIComponent(paper.id)}`)} className="bg-purple-50 text-purple-700 font-bold py-1.5 px-3 rounded-lg hover:bg-purple-100 text-xs transition">📅 IQ Study Tracker</button>}
                            {/* --- INTEGRATED: INSTIL IQ ADMIN LAUNCHER --- */}
                            <button onClick={() => onViewInstilIQ(paper)} className="bg-pink-50 text-[#D02C70] font-bold py-1.5 px-3 rounded-lg hover:bg-pink-100 text-xs transition border border-pink-100">🧠 Instil IQ</button>
                            
                            <button onClick={() => onViewExams(paper)} className="bg-green-500 text-white font-bold py-1.5 px-3 rounded-lg hover:bg-green-600 text-xs transition">View Exams</button>
                            <button onClick={() => setPaperToEdit(paper)} className="bg-yellow-400 text-white font-bold py-1.5 px-3 rounded-lg hover:bg-yellow-500 text-xs transition">Edit Syllabus</button>
                            
                            {/* Hide Duplicate/Delete for restricted admins */}
                            {(!assignedPapers) && (
                                <>
                                    <button onClick={() => setPaperToDuplicate(paper)} className="bg-indigo-50 text-indigo-600 font-bold py-1.5 px-3 rounded-lg hover:bg-indigo-100 text-xs transition border border-indigo-100">📄 Duplicate</button>
                                    <button onClick={() => setPaperToDelete(paper.id)} className="bg-red-50 text-red-500 font-bold py-1.5 px-3 rounded-lg hover:bg-red-100 text-xs transition border border-red-100">&times; Delete</button>
                                </>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default PaperListAdmin;